import AuthGuard from '@/components/auth-guard';
import { FeatureGuard } from '@/components/feature-guard';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import { useNotes } from '@/hooks/useNotes';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { NoteSummary } from '@common/schemas/notes.schema';
import { FormatListBulleted, GridView, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight, Close } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  NoteDetailsPanel,
  NoteEditor,
  NoteList,
  NotesSidebar
} from '@/features/notes/components';
import { Divider, IconButton } from '@mui/material';

export const Route = createFileRoute('/notes')({
  component: NotesRoute,
});

type CollectionType = 'all_notes' | 'recent' | 'favorites' | 'imported' | 'unsorted';

function NotesContent() {
  const { t } = useTranslation();
  const { novel, updateNovelItem, getNewId, contentDao } = useNovelRepository();
  const { notes, createNote, updateNote } = useNotes();

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<CollectionType | null>('all_notes');
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState<NoteSummary | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingInternalAction, setPendingInternalAction] = useState<(() => void) | null>(null);

  const [showDetailsPanel, setShowDetailsPanel] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isListCollapsed, setIsListCollapsed] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState<string | null>(null);
  const [isFolderRenameDialogOpen, setIsFolderRenameDialogOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState<string | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');

  const [isFolderDeleteDialogOpen, setIsFolderDeleteDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{ id: string, name: string } | null>(null);

  const folders = useMemo(() => {
    return Object.entries(novel.noteFolders).map(([id, name]) => ({ id, name, parentId: novel.noteFolderParents?.[id] || null }));
  }, [novel.noteFolders]);

  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    withResolver: true,
    enableBeforeUnload: false,
  });

  // Filter notes based on folder/collection and search query
  const filteredNotes = useMemo(() => {
    let filtered = notes || [];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(n => n.title.toLowerCase().includes(q) || n.preview?.toLowerCase().includes(q));
    }

    if (selectedFolderId) {
      filtered = filtered.filter(n => n.folderId === selectedFolderId);
    } else if (selectedCollection) {
      switch (selectedCollection) {
        case 'favorites':
          filtered = filtered.filter(n => n.isFavorite);
          break;
        case 'imported':
          filtered = filtered.filter(n => !!n.sourceImportSessionId);
          break;
        case 'unsorted':
          filtered = filtered.filter(n => !n.folderId);
          break;
        default:
          break;
      }
    }

    filtered = filtered.sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());

    if (draftNote && selectedNoteId === draftNote.id) {
      const realNoteExists = notes?.some(n => n.id === draftNote.id);
      if (!realNoteExists) {
        filtered = [draftNote, ...filtered];
      }
    }

    return filtered;
  }, [notes, selectedFolderId, selectedCollection, searchQuery, draftNote, selectedNoteId]);

  const checkUnsaved = (action: () => void) => {
    if (isDirty) {
      setPendingInternalAction(() => action);
      return false;
    }
    return true;
  };

  const handleSelectNote = (noteId: string) => {
    if (checkUnsaved(() => setSelectedNoteId(noteId))) {
      setSelectedNoteId(noteId);
    }
  };

  const handleCreateNote = () => {
    const action = () => {
      const newId = getNewId();
      const newContentId = getNewId();
      const now = new Date().toISOString();
      setDraftNote({
        id: newId,
        title: '',
        preview: '',
        isFavorite: false,
        folderId: selectedFolderId || null,
        updatedAt: now,
        contentId: newContentId,
        linkedEntityIds: [],
        tags: []
      });
      setSelectedNoteId(newId);
    };
    if (checkUnsaved(action)) action();
  };

  const handleSelectFolder = (folderId: string) => {
    const action = () => {
      setSelectedFolderId(folderId);
      setSelectedCollection(null);
      setSelectedNoteId(null);
    };
    if (checkUnsaved(action)) action();
  };

  const handleSelectCollection = (collection: CollectionType) => {
    const action = () => {
      setSelectedCollection(collection);
      setSelectedFolderId(null);
      setSelectedNoteId(null);
    };
    if (checkUnsaved(action)) action();
  };

  const handleCreateFolderSubmit = async () => {
    if (!newFolderName.trim() || !novel.id) return;
    const folderId = getNewId();
    await updateNovelItem({
      noteFolders: { ...novel.noteFolders, [folderId]: newFolderName.trim() },
      noteFolderParents: newFolderParentId ? { ...(novel.noteFolderParents || {}), [folderId]: newFolderParentId } : (novel.noteFolderParents || {})
    });
    setNewFolderName('');
    setNewFolderParentId(null);
    setIsFolderDialogOpen(false);
    handleSelectFolder(folderId);
  };

  const handleEditFolder = (id: string, currentName: string) => {
    setFolderToRename(id);
    setRenameFolderName(currentName);
    setIsFolderRenameDialogOpen(true);
  };

  const handleRenameFolderSubmit = async () => {
    if (!renameFolderName.trim() || !novel.id || !folderToRename) return;
    await updateNovelItem({
      noteFolders: {
        ...novel.noteFolders,
        [folderToRename]: renameFolderName.trim()
      }
    });
    setIsFolderRenameDialogOpen(false);
    setFolderToRename(null);
  };

  const handleDeleteFolderRequest = (id: string, name: string) => {
    setFolderToDelete({ id, name });
    setIsFolderDeleteDialogOpen(true);
  };

  const handleDeleteFolderConfirm = async () => {
    if (!novel.id || !folderToDelete) return;
    const updatedFolders = { ...novel.noteFolders };
    delete updatedFolders[folderToDelete.id];
    const updatedParents = { ...(novel.noteFolderParents || {}) };
    delete updatedParents[folderToDelete.id];
    Object.keys(updatedParents).forEach((id) => { if (updatedParents[id] === folderToDelete.id) delete updatedParents[id]; });

    await updateNovelItem({ noteFolders: updatedFolders, noteFolderParents: updatedParents });

    if (selectedFolderId === folderToDelete.id) {
      setSelectedFolderId(null);
      setSelectedCollection('all_notes');
    }

    setIsFolderDeleteDialogOpen(false);
    setFolderToDelete(null);
  };

  const convertHtmlToPlainText = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const handleSaveRequest = async (draft: Partial<NoteSummary>, content: string) => {
    if (!novel.id || !contentDao) return;

    const plainText = convertHtmlToPlainText(content);

    try {
      if (draftNote && selectedNoteId === draftNote.id) {
        const newNote = await createNote(
          draft.title || t('research_notes.editor.untitled'),
          draftNote.folderId || undefined,
          draftNote.id,
          draftNote.contentId
        );

        if (newNote.contentId) {
          const wordCount = plainText.split(/\s+/).filter(Boolean).length;
          await contentDao.updateContent(novel.ownerId, novel, newNote.contentId, {
            scratchpad: content,
            wordCount,
          });
        }
        if (draft.isFavorite) {
          await updateNote(newNote.id, { isFavorite: true });
        }
        await updateNote(newNote.id, { preview: plainText.slice(0, 150) });

      } else if (selectedNoteId) {
        await updateNote(selectedNoteId, {
          title: draft.title,
          isFavorite: draft.isFavorite,
          preview: plainText.slice(0, 150)
        });
        const note = notes?.find(n => n.id === selectedNoteId);
        if (note?.contentId) {
          const wordCount = plainText.split(/\s+/).filter(Boolean).length;
          await contentDao.updateContent(novel.ownerId, novel, note.contentId, {
            scratchpad: content,
            wordCount,
          });
        }
      }
    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  const handleDiscardChanges = () => {
    setIsDirty(false);

    if (blocker.status === 'blocked') {
      blocker.proceed();
    }

    if (pendingInternalAction) {
      pendingInternalAction();
      setPendingInternalAction(null);
    }
  };

  const handleCancelNavigation = () => {
    if (blocker.status === 'blocked') {
      blocker.reset();
    }
    setPendingInternalAction(null);
  };

  const realNote = notes?.find(n => n.id === selectedNoteId);
  const selectedNote = (draftNote && selectedNoteId === draftNote.id && !realNote)
    ? draftNote
    : realNote;

  return (
    <>
      <Page
        title={t('research_notes.title')}
        description={t('research_notes.subtitle')}
        layoutMode="workspace"
        actions={
          <ToggleButtonGroup size="small" value="list" exclusive>
            <ToggleButton value="list" sx={{ textTransform: 'none' }}>
              <FormatListBulleted fontSize="small" sx={{ mr: 1 }} />
              {t('ui.list')}
            </ToggleButton>
            <ToggleButton value="grid" disabled sx={{ textTransform: 'none' }}>
              <GridView fontSize="small" sx={{ mr: 1 }} />
              {t('ui.grid')}
            </ToggleButton>
          </ToggleButtonGroup>
        }
      >
        <Box sx={{
          display: 'flex',
          flexGrow: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          width: '100%'
        }}>
          {/* 1. Sidebar Column */}
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={mobileSidebarOpen}
              onClose={() => setMobileSidebarOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{ '& .MuiDrawer-paper': { width: 240, boxSizing: 'border-box' } }}
            >
              <NotesSidebar
                selectedFolderId={selectedFolderId}
                selectedCollection={selectedCollection}
                onSelectFolder={(id) => { handleSelectFolder(id); setMobileSidebarOpen(false); }}
                onSelectCollection={(c) => { handleSelectCollection(c); setMobileSidebarOpen(false); }}
                folders={folders}
                onCreateFolder={(parentId) => { setNewFolderParentId(parentId || null); setIsFolderDialogOpen(true); }}
                onEditFolder={handleEditFolder}
                onDeleteFolder={handleDeleteFolderRequest}
              />
            </Drawer>
          ) : (
            <Box sx={{
              width: isSidebarCollapsed ? 48 : 240,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              transition: 'width 0.2s',
              overflow: 'hidden'
            }}>
              <Box sx={{ flexGrow: 1, overflow: 'hidden', display: isSidebarCollapsed ? 'none' : 'block' }}>
                <NotesSidebar
                  selectedFolderId={selectedFolderId}
                  selectedCollection={selectedCollection}
                  onSelectFolder={handleSelectFolder}
                  onSelectCollection={handleSelectCollection}
                  folders={folders}
                  onCreateFolder={(parentId) => { setNewFolderParentId(parentId || null); setIsFolderDialogOpen(true); }}
                  onEditFolder={handleEditFolder}
                  onDeleteFolder={handleDeleteFolderRequest}
                />
              </Box>
              <Divider />
              <Box sx={{ p: 1, display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'flex-end', bgcolor: 'background.paper' }}>
                <IconButton size="small" onClick={() => {
                  if (isSidebarCollapsed && isListCollapsed) {
                    setIsSidebarCollapsed(false);
                    setIsListCollapsed(false);
                  } else {
                    setIsSidebarCollapsed(!isSidebarCollapsed);
                  }
                }}>
                  {isSidebarCollapsed ? <KeyboardDoubleArrowRight fontSize="small" /> : <KeyboardDoubleArrowLeft fontSize="small" />}
                </IconButton>
              </Box>
            </Box>
          )}

          {/* 2. Middle Column: List of Notes */}
          {(!(isSidebarCollapsed && isListCollapsed && !isMobile) && (!isMobile || !selectedNoteId)) && (
            <Box sx={{
              width: { xs: '100%', md: 350 },
              flexShrink: 0,
              borderRight: { xs: 'none', md: '1px solid' },
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              transition: 'width 0.2s',
              overflow: 'hidden'
            }}>
              <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                <NoteList
                  notes={filteredNotes}
                  selectedNoteId={selectedNoteId}
                  onSelectNote={handleSelectNote}
                  onCreateNote={handleCreateNote}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  isMobile={isMobile}
                  onOpenSidebar={() => setMobileSidebarOpen(true)}
                />
              </Box>
              
              {!isMobile && isSidebarCollapsed && (
                <>
                  <Divider />
                  <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', bgcolor: 'background.paper' }}>
                    <IconButton size="small" onClick={() => setIsListCollapsed(true)}>
                      <KeyboardDoubleArrowLeft fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* 3. Main Column: Editor */}
          {(!isMobile || selectedNoteId) && (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {selectedNoteId && selectedNote ? (
                <NoteEditor
                  key={selectedNoteId}
                  noteId={selectedNoteId}
                  noteSummary={selectedNote as NoteSummary}
                  isDraft={selectedNote === draftNote}
                  onToggleDetails={() => setShowDetailsPanel(prev => !prev)}
                  isDetailsVisible={showDetailsPanel}
                  onDeleted={() => {
                    setIsDirty(false);
                    setSelectedNoteId(null);
                  }}
                  onDirtyChange={setIsDirty}
                  onSaveRequest={handleSaveRequest}
                  onBackToList={isMobile ? () => setSelectedNoteId(null) : undefined}
                />
              ) : selectedNoteId ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                  {t('research_notes.empty_state.create_first')}
                </Box>
              )}
            </Box>
          )}

          {/* 4. Right Column: Metadata Panel */}
          {showDetailsPanel && selectedNoteId && selectedNote && (
            <Box sx={{
              width: { xs: '100%', md: 300 },
              flexShrink: 0,
              borderLeft: { xs: 'none', md: '1px solid' },
              borderColor: 'divider',
              bgcolor: 'background.paper',
              ...(isMobile ? {
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 10
              } : {})
            }}>
              {isMobile && (
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid', borderColor: 'divider' }}>
                   <IconButton onClick={() => setShowDetailsPanel(false)}><Close /></IconButton>
                </Box>
              )}
              <NoteDetailsPanel
                noteId={selectedNoteId}
                noteSummary={selectedNote as NoteSummary}
                onUpdateDraftFolder={(folderId) => {
                  if (draftNote && selectedNoteId === draftNote.id) {
                    setDraftNote(prev => prev ? { ...prev, folderId } : null);
                  }
                }}
                onUpdateDraftTags={(tags) => {
                  if (draftNote && selectedNoteId === draftNote.id) {
                    setDraftNote(prev => prev ? { ...prev, tags } : null);
                  }
                }}
                onUpdateDraftLinkedEntities={(linkedEntityIds) => {
                  if (draftNote && selectedNoteId === draftNote.id) {
                    setDraftNote(prev => prev ? { ...prev, linkedEntityIds } : null);
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Page>

      {/* Folder Creation Dialog */}
      <Dialog open={isFolderDialogOpen} onClose={() => setIsFolderDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('research_notes.new_folder')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('research_notes.note_details.folder')}
            type="text"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolderSubmit();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFolderDialogOpen(false)}>{t('ui.delete_modal.cancel')}</Button>
          <Button onClick={handleCreateFolderSubmit} variant="contained" disabled={!newFolderName.trim()}>
            {t('ui.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Folder Rename Dialog */}
      <Dialog open={isFolderRenameDialogOpen} onClose={() => setIsFolderRenameDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('ui.rename')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('research_notes.note_details.folder')}
            type="text"
            fullWidth
            variant="outlined"
            value={renameFolderName}
            onChange={(e) => setRenameFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameFolderSubmit();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFolderRenameDialogOpen(false)}>{t('ui.delete_modal.cancel')}</Button>
          <Button onClick={handleRenameFolderSubmit} variant="contained" disabled={!renameFolderName.trim()}>
            {t('ui.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Folder Delete Modal */}
      <DeleteConfirmationModal
        isOpen={isFolderDeleteDialogOpen}
        onClose={() => setIsFolderDeleteDialogOpen(false)}
        onConfirm={handleDeleteFolderConfirm}
        itemName={folderToDelete?.name || 'Folder'}
        confirmationText="DELETE"
      />

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={blocker.status === 'blocked' || pendingInternalAction !== null}
        onClose={handleCancelNavigation}
        maxWidth="xs"
      >
        <DialogTitle>{t('research_notes.editor.unsaved_changes')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {'You have unsaved changes. Do you want to discard them?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation}>{t('ui.delete_modal.cancel')}</Button>
          <Button onClick={handleDiscardChanges} color="error" variant="contained">
            {t('ui.discard_changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function NotesRoute() {
  return (
    <AuthGuard>
      <FeatureGuard featureKey="should_display_research_and_notes_page">
        <NotesContent />
      </FeatureGuard>
    </AuthGuard>
  );
}
